
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface WeaponItem {
  name: string;
  price: number;
  damage: number;
  magazine: number;
  fireRate: string;
}

interface AmmoItem {
  name: string;
  caliber: string;
  packaging: string;
  rounds: number;
  price: number;
  compatible: string;
}

interface CartItem {
  name: string;
  price: number;
  quantity: number;
}

const WeaponsShopPage = () => {
  const [activeTab, setActiveTab] = useState<'weapons' | 'ammunition'>('weapons');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountPercent, setDiscountPercent] = useState<number>(0);

  const weapons: WeaponItem[] = [
    { name: "Beretta M9", price: 900, damage: 11.2, magazine: 15, fireRate: "10/s" },
    { name: "Desert Eagle", price: 1300, damage: 20, magazine: 10, fireRate: "10/s" },
    { name: "Colt M1911", price: 1200, damage: 13, magazine: 12, fireRate: "11/s" },
    { name: "Colt Python", price: 1250, damage: 23, magazine: 6, fireRate: "1/s" },
    { name: "Remington 870", price: 1900, damage: 3, magazine: 7, fireRate: "1/s" },
    { name: "AK-47", price: 1500, damage: 10.5, magazine: 30, fireRate: "13/s" },
    { name: "LMT L129A1", price: 2300, damage: 16, magazine: 20, fireRate: "12/s" },
    { name: "M14", price: 2000, damage: 24.3, magazine: 20, fireRate: "3/s" },
    { name: "Remington 700", price: 2000, damage: 65, magazine: 5, fireRate: "1/s" },
    { name: "Remington MSR", price: 2500, damage: 75, magazine: 6, fireRate: "1/s" },
    { name: "PPSH 41", price: 1500, damage: 10.5, magazine: 30, fireRate: "13/s" },
    { name: "Kriss Vector", price: 1500, damage: 7.3, magazine: 30, fireRate: "20/s" }
  ];

  const ammunition: AmmoItem[] = [
    { name: '9×19mm Box', caliber: '9×19mm', packaging: 'Box', rounds: 200, price: 120, compatible: 'Beretta M9, Colt M1911, Kriss Vector' },
    { name: '.50 Action Express Box', caliber: '.50 AE', packaging: 'Box', rounds: 100, price: 180, compatible: 'Desert Eagle' },
    { name: '.44 Magnum Box', caliber: '.44 Magnum', packaging: 'Box', rounds: 100, price: 160, compatible: 'Desert Eagle' },
    { name: '.357 Magnum Box', caliber: '.357 Magnum', packaging: 'Box', rounds: 100, price: 150, compatible: 'Desert Eagle, Colt Python' },
    { name: '.45 ACP Box', caliber: '.45 ACP', packaging: 'Box', rounds: 200, price: 130, compatible: 'Colt M1911, Kriss Vector' },
    { name: '7.62×39mm Box', caliber: '7.62×39mm', packaging: 'Box', rounds: 200, price: 140, compatible: 'AK-47' },
    { name: '7.62×51mm Box', caliber: '7.62×51mm NATO', packaging: 'Box', rounds: 200, price: 170, compatible: 'LMT L129A1, M14, Remington 700, Remington MSR' },
    { name: '12 GA Birdshot Box', caliber: '12 gauge', packaging: 'Box', rounds: 100, price: 110, compatible: 'Remington 870' }
  ];

  const addToCart = (name: string, price: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.name === name);
      if (existing) {
        return prev.map(item =>
          item.name === name ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { name, price, quantity: 1 }];
    });
  };

  const removeFromCart = (name: string) => {
    setCart(prev => prev.filter(item => item.name !== name));
  };

  const updateQuantity = (name: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(name);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.name === name ? { ...item, quantity } : item
      )
    );
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = subtotal * (discountPercent / 100);
  const taxableAmount = subtotal - discountAmount;
  const tax = taxableAmount * 0.04;
  const total = taxableAmount + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg" style={{ fontFamily: '"Pacifico", serif' }}>TA</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>Tactical Arms</h1>
                <p className="text-xs text-orange-400">Weapons & Ammunition Pricing Reference</p>
              </div>
            </Link>
            
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-300 hover:text-orange-400 transition-colors">Home</Link>
              <Link to="/official-discounts" className="text-gray-300 hover:text-orange-400 transition-colors">Official Discounts</Link>
              <Link to="/shop" className="text-gray-300 hover:text-orange-400 transition-colors">Shop</Link>
            </nav>
            
            <div className="text-gray-400 text-sm">All prices in USD</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Price Calculator */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Price Calculator</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Cart */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Selected Items</h3>
              <div className="bg-gray-900/50 rounded-xl p-4 min-h-[200px] max-h-[300px] overflow-y-auto">
                {cart.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No items selected</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.name} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-gray-400 text-sm">${item.price.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.name, item.quantity - 1)}
                            className="w-8 h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-12 text-center text-white font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.name, item.quantity + 1)}
                            className="w-8 h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Totals */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label htmlFor="discount" className="text-white font-medium">Discount %:</label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(Number(e.target.value))}
                  className="w-20 px-3 py-2 bg-gray-800 border border-orange-500/30 rounded-lg text-white text-right focus:outline-none focus:border-orange-500"
                />
              </div>
              
              <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal:</span>
                  <span>${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Discount ({discountPercent}%):</span>
                  <span>-${discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Sales Tax (4.00%):</span>
                  <span>${tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t border-gray-700 pt-3">
                  <div className="flex justify-between text-orange-400 font-bold text-lg">
                    <span>Total:</span>
                    <span>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-800/50 rounded-xl p-1 flex">
            <button
              onClick={() => setActiveTab('weapons')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === 'weapons'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Weapons
            </button>
            <button
              onClick={() => setActiveTab('ammunition')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === 'ammunition'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Ammunition
            </button>
          </div>
        </div>

        {/* Weapons Table */}
        {activeTab === 'weapons' && (
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Select</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Weapon</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Price</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Damage</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Magazine</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Fire Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {weapons.map((weapon) => (
                    <tr key={weapon.name} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={cart.some(item => item.name === weapon.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addToCart(weapon.name, weapon.price);
                            } else {
                              removeFromCart(weapon.name);
                            }
                          }}
                          className="w-5 h-5 text-orange-500 bg-gray-800 border-orange-500 rounded focus:ring-orange-500 focus:ring-2"
                        />
                      </td>
                      <td className="py-4 px-4 text-white font-medium">{weapon.name}</td>
                      <td className="py-4 px-4 text-gray-300">${weapon.price.toLocaleString()}</td>
                      <td className="py-4 px-4 text-gray-300">{weapon.damage}</td>
                      <td className="py-4 px-4 text-gray-300">{weapon.magazine}</td>
                      <td className="py-4 px-4 text-gray-300">{weapon.fireRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ammunition Table */}
        {activeTab === 'ammunition' && (
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Select</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Ammo</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Caliber</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Packaging</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Rounds</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Price</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Compatible</th>
                  </tr>
                </thead>
                <tbody>
                  {ammunition.map((ammo) => (
                    <tr key={ammo.name} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={cart.some(item => item.name === ammo.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addToCart(ammo.name, ammo.price);
                            } else {
                              removeFromCart(ammo.name);
                            }
                          }}
                          className="w-5 h-5 text-orange-500 bg-gray-800 border-orange-500 rounded focus:ring-orange-500 focus:ring-2"
                        />
                      </td>
                      <td className="py-4 px-4 text-white font-medium">{ammo.name}</td>
                      <td className="py-4 px-4 text-gray-300">{ammo.caliber}</td>
                      <td className="py-4 px-4 text-gray-300">{ammo.packaging}</td>
                      <td className="py-4 px-4 text-gray-300">{ammo.rounds}</td>
                      <td className="py-4 px-4 text-gray-300">${ammo.price.toLocaleString()}</td>
                      <td className="py-4 px-4 text-gray-300 text-sm">{ammo.compatible}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-orange-500/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">© 2024 Tactical Arms - NXT Cam Properties</p>
              <p className="text-gray-500 text-sm">Retail Reference Only</p>
            </div>
            <div className="flex items-center space-x-6">
              <a href="https://readdy.ai/?origin=logo" className="text-gray-500 hover:text-orange-400 transition-colors text-sm">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WeaponsShopPage;
